import { Plus, Calendar, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fetchMyEvents, type EventRow } from "@/lib/api/events";
import { LinkButton } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { PageHeadline } from "@/components/ui/Headline";
import { Loader, EmptyState, ErrorBlock } from "@/components/ui/States";
import { OrganizerShell } from "@/components/layout/OrganizerShell";

function formatEventDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function formatRevealCountdown(event: EventRow): string {
  const revealMs =
    new Date(event.event_date).getTime() - event.match_reveal_hours * 3600 * 1000;
  const delta = revealMs - Date.now();
  if (delta <= 0) {
    if (new Date(event.event_date).getTime() < Date.now()) return "Event finished";
    return "Matches live";
  }
  const days = Math.floor(delta / 86_400_000);
  const hours = Math.floor((delta % 86_400_000) / 3_600_000);
  if (days > 0) return `${days}d ${hours}h to reveal`;
  const minutes = Math.floor((delta % 3_600_000) / 60_000);
  return `${hours}h ${minutes}m to reveal`;
}

export function MyEventsPage() {
  const {
    data: events,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["my-events"],
    queryFn: fetchMyEvents,
  });

  const active = (events ?? []).filter((e) => e.status === "active");
  const past = (events ?? []).filter((e) => e.status !== "active");

  return (
    <OrganizerShell>
      <div className="animate-fade-in">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <PageHeadline title="My Events" subtitle="Active and past events you organize" />
          <LinkButton to="/events/new" leadingIcon={<Plus className="h-4 w-4" />}>
            Create New Event
          </LinkButton>
        </div>

        {isLoading ? (
          <Loader label="Loading your events..." />
        ) : error ? (
          <ErrorBlock
            title="Could not load events"
            message={(error as Error).message}
            onRetry={() => refetch()}
          />
        ) : !events || events.length === 0 ? (
          <Card padding="lg" className="mt-8 text-center">
            <EmptyState
              icon="📅"
              title="No events yet"
              description="Create your first Finally event in 15 minutes — invite participants and watch the matches roll in."
              action={
                <LinkButton to="/events/new" leadingIcon={<Plus className="h-4 w-4" />}>
                  Create your first event
                </LinkButton>
              }
            />
          </Card>
        ) : (
          <>
            {active.length > 0 ? (
              <Section title="Active" count={active.length}>
                {active.map((e) => (
                  <ActiveEventCard key={e.id} event={e} />
                ))}
              </Section>
            ) : null}

            {past.length > 0 ? (
              <Section title="Past" count={past.length}>
                {past.map((e) => (
                  <PastEventCard key={e.id} event={e} />
                ))}
              </Section>
            ) : null}
          </>
        )}
      </div>
    </OrganizerShell>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-[15px] font-bold text-[#1a1a1a]">{title}</h2>
        <Badge tone="neutral">{count}</Badge>
      </div>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

function ActiveEventCard({ event }: { event: EventRow }) {
  return (
    <Card tone="default" hoverable padding="md">
      <Link to={`/events/${event.id}`} className="block no-underline">
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-[18px] font-bold text-[#1a1a1a]">{event.title}</h3>
          <Badge tone="info">Active</Badge>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#6c757d]">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" /> {formatEventDate(event.event_date)}
          </span>
          {event.location ? (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> {event.location}
            </span>
          ) : null}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat label="Registered" value={String(event.participant_count ?? 0)} />
          <Stat label="Reveal" value={formatRevealCountdown(event)} />
          <Stat label="Status" value="Live" tone="info" />
        </div>
      </Link>
    </Card>
  );
}

function PastEventCard({ event }: { event: EventRow }) {
  const cancelled = event.status === "cancelled";
  return (
    <Card tone={cancelled ? "danger" : "default"} padding="md">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[16px] font-bold text-[#1a1a1a]">{event.title}</h3>
          <p className="mt-1 text-[13px] text-[#6c757d]">
            {formatEventDate(event.event_date)}
          </p>
        </div>
        <Badge tone={cancelled ? "danger" : "neutral"}>{event.status}</Badge>
      </div>
      <div className="mt-3 flex flex-wrap gap-3">
        <Stat label="Registered" value={String(event.participant_count ?? 0)} />
        {!cancelled ? (
          <Link
            to={`/events/${event.id}/report`}
            className="ml-auto self-end rounded-[10px] border border-[#e2e8f0] bg-white px-3 py-1.5 text-[13px] font-semibold text-[#1a1a1a] hover:bg-[#f8fafc]"
          >
            View Report →
          </Link>
        ) : null}
      </div>
    </Card>
  );
}

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "info";
}) {
  return (
    <div className="rounded-[10px] bg-[#f8fafc] px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">{label}</p>
      <p
        className={
          "mt-0.5 text-[14px] font-bold " + (tone === "info" ? "text-[#2563eb]" : "text-[#1a1a1a]")
        }
      >
        {value}
      </p>
    </div>
  );
}
